import type { ComponentPropsWithRef, PropsWithChildren } from 'react'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import cn from 'classnames'
import { useId, useState } from 'react'
import { escapeCssIdent } from './escapeCssIdent'
import styles from './Menu.module.css'

function MenuList({ ref, className, ...props }: ComponentPropsWithRef<typeof NavigationMenu.List>) {
  return <NavigationMenu.List ref={ref} className={cn(styles.list, className)} {...props} />
}
MenuList.displayName = 'Menu.List'

function MenuItem({ ref, className, ...props }: ComponentPropsWithRef<typeof NavigationMenu.Item>) {
  return <NavigationMenu.Item ref={ref} className={cn(styles.item, className)} {...props} />
}
MenuItem.displayName = 'Menu.Item'

Menu.List = MenuList
Menu.Item = MenuItem

export function Menu({ children }: PropsWithChildren) {
  const [valueMenu, setValueMenu] = useState('')
  // Every main nav item renders its own <Menu>, all force-mounted at once for SEO, so multiple
  // instances of this component exist in the DOM simultaneously. They all share the same CSS
  // Modules class name, so a plain class selector would leak across instances - scope the
  // injected rule to this instance's own viewport via a unique id.
  const viewportId = useId()

  return (
    <NavigationMenu.Content onPointerLeave={(e: React.PointerEvent) => e.preventDefault()} forceMount>
      <ScrollArea.Root>
        <ScrollArea.Viewport className={styles.menuContent}>
          <NavigationMenu.Sub className={styles.menu} orientation="vertical" value={valueMenu} onValueChange={setValueMenu}>
            <MenuList>{children}</MenuList>
            <div className={styles.activeContent}>
              {/* Radix force-mounts every item's Content for SEO, so we only show the active
                  one via a plain CSS attribute selector matching its auto-generated id suffix.
                  This stays declarative (recomputed on every render from React state) instead
                  of imperatively toggling styles, which would race with Radix mounting new
                  Content nodes into the viewport asynchronously. */}
              <style dangerouslySetInnerHTML={{ __html: `#${escapeCssIdent(viewportId)} > :not([id$="${escapeCssIdent(valueMenu)}"]) { display: none; }` }} />
              <NavigationMenu.Viewport id={viewportId} className={styles.viewport} />
            </div>
          </NavigationMenu.Sub>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation="vertical">
          <ScrollArea.Thumb />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    </NavigationMenu.Content>
  )
}
